
  import Vue from 'vue'
  import Component from 'vue-class-component'
  import { Prop } from 'vue-property-decorator'
  import { IServiceProvider } from '@/model/Service/serviceProviderInterfaces'
  import { IErrors } from '@/model/IErrors'

  const EXAMPLE_PRICE = 50
  // Stripe rejects card charges under 0.50 €
  const MIN_CHARGE = 0.5

  @Component({})
  export default class SPStripeDiscount extends Vue {

    @Prop() protected provider!: IServiceProvider
    @Prop() protected errors!: IErrors
    @Prop() protected editing!: boolean
    @Prop() protected viewing!: boolean
    @Prop() protected disabled!: boolean

    protected get primaryColor(): string {
      const v = this.$vuetify
      return (v && v.theme && v.theme.currentTheme && (v.theme.currentTheme.primary as string)) || '#1976D2'
    }

    protected get headerIconWrapStyle(): object {
      return { background: this.primaryColor }
    }

    protected get isPercentage(): boolean {
      return this.provider.stripe_discount_type === 'percentage'
    }

    protected get value(): number {
      return Number(this.provider.stripe_discount_value)
    }

    protected get hasDiscount(): boolean {
      return !!this.provider.stripe_discount_type && this.value > 0
    }

    protected get summary(): string {
      if (!this.hasDiscount) {
        return this.$t('services.provider.fields.stripe_discount_none') as string
      }

      return this.isPercentage
        ? '-' + this.formatNumber(this.value) + ' %'
        : '-' + this.formatCurrency(this.value)
    }

    protected get discountTypeItems() {
      const values: any = this.$t('services.provider.fields.stripe_discount_type_values')
      return Object.keys(values).map(key => ({ text: values[key], value: key }))
    }

    // What the customer pays for an example price, following the backend rules
    protected get example(): string {
      if (!this.hasDiscount) {
        return ''
      }

      const wanted = this.isPercentage ? Math.round(EXAMPLE_PRICE * this.value) / 100 : this.value
      if (wanted >= EXAMPLE_PRICE) {
        return this.$t('services.provider.fields.stripe_discount_example_free', {
          price: this.formatCurrency(EXAMPLE_PRICE),
        }) as string
      }

      const discount = Math.min(wanted, EXAMPLE_PRICE - MIN_CHARGE)

      return this.$t('services.provider.fields.stripe_discount_example', {
        price: this.formatCurrency(EXAMPLE_PRICE),
        discount: this.formatCurrency(discount),
        total: this.formatCurrency(EXAMPLE_PRICE - discount),
      }) as string
    }

    protected onTypeChange(type: string | null) {
      if (!type) {
        this.provider.stripe_discount_value = null
      }
    }

    private formatCurrency(amount: number): string {
      return new Intl.NumberFormat(this.$i18n.locale, { style: 'currency', currency: 'EUR' }).format(amount)
    }

    private formatNumber(amount: number): string {
      return new Intl.NumberFormat(this.$i18n.locale, { maximumFractionDigits: 2 }).format(amount)
    }
  }
